import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  CalendarDays, Clock4, Users, Sparkles, Video, MapPin, Loader2, ArrowLeft, CheckCircle2,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import SpeakerAvatar from "../components/SpeakerAvatar";
import WebinarRegistrationModal from "../components/WebinarRegistrationModal";
import { api } from "../lib/api";
import { formatWebinarDate, formatTimeRange } from "../lib/webinarFormat";
import { trackWebinarCardView, trackWebinarRegistrationStart } from "../lib/tracking";

const SITE_ORIGIN = "https://ai.fped.org";

function buildEventJsonLd(w) {
  if (!w) return null;
  const url = `${SITE_ORIGIN}/webinarii/${w.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: w.title,
    description: w.short_description || w.full_description || w.title,
    startDate: `${w.date}T${w.start_time || "00:00"}:00`,
    eventAttendanceMode:
      w.location_type === "online"
        ? "https://schema.org/OnlineEventAttendanceMode"
        : "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    organizer: {
      "@type": "Organization",
      name: w.organizer_name || "Fundația pentru Educație Digitală",
      url: "https://fped.org",
    },
    location:
      w.location_type === "online"
        ? { "@type": "VirtualLocation", url }
        : { "@type": "Place", name: "Locație fizică" },
    ...(w.speaker_name ? { performer: { "@type": "Person", name: w.speaker_name } } : {}),
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "RON",
      availability: "https://schema.org/InStock",
      url,
    },
  };
}

export default function WebinarDetail() {
  const { slug } = useParams();
  const [webinar, setWebinar] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound | error
  const [registerOpen, setRegisterOpen] = useState(false);
  const [successId, setSuccessId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    api.get(`/webinars/public/${encodeURIComponent(slug)}`).then((r) => {
      if (cancelled) return;
      const w = r.data?.webinar;
      if (!w) { setStatus("notfound"); return; }
      setWebinar(w);
      setStatus("ok");
      trackWebinarCardView(w);
    }).catch((err) => {
      if (cancelled) return;
      setStatus(err?.response?.status === 404 ? "notfound" : "error");
    });
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (webinar?.title) document.title = `${webinar.title} | Webinarii FPED`;
  }, [webinar]);

  const isPast = webinar?.status === "completed";
  const isFull = webinar?.status === "full"
    || (webinar?.capacity > 0 && webinar?.seats_remaining === 0);
  const canRegister = status === "ok" && !isPast && !isFull;

  const openRegistration = () => {
    setSuccessId(null);
    setRegisterOpen(true);
    trackWebinarRegistrationStart(webinar);
  };

  return (
    <div className="min-h-screen bg-[#F5F6F6]" data-testid="webinar-detail-page">
      <SEO
        title={webinar ? `${webinar.title} | Webinarii FPED` : "Webinar | FPED"}
        description={webinar?.short_description || "Webinar gratuit despre AI în educație, organizat de Fundația pentru Educație Digitală."}
        canonical={`${SITE_ORIGIN}/webinarii/${slug}`}
        image={webinar?.speaker_photo_url || undefined}
        ogType="article"
        jsonLd={buildEventJsonLd(webinar)}
      />
      <Header />
      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-10 md:py-14">
        <Link
          to="/webinarii"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#965EA7] hover:text-[#7A4986]"
          data-testid="webinar-detail-back"
        >
          <ArrowLeft size={15} /> Toate webinariile
        </Link>

        {status === "loading" && (
          <div className="mt-8 bg-white rounded-3xl border border-[#E5E7EB] p-10 text-center text-[#6B7280]">
            <Loader2 className="animate-spin mx-auto mb-3" size={24} /> Se încarcă webinarul...
          </div>
        )}

        {(status === "notfound" || status === "error") && (
          <div className="mt-8 bg-white rounded-3xl border border-[#E5E7EB] p-10 md:p-14 text-center" data-testid="webinar-detail-notfound">
            <h1 className="font-display text-2xl font-bold text-[#373A45]">
              {status === "notfound" ? "Webinarul nu a fost găsit" : "A apărut o eroare"}
            </h1>
            <p className="mt-2 text-[#6B7280]">
              {status === "notfound"
                ? "Este posibil ca acest webinar să fi fost retras sau linkul să fie greșit."
                : "Te rugăm să încerci din nou peste câteva momente."}
            </p>
            <Link
              to="/webinarii"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold px-6 py-3"
            >
              Vezi webinariile disponibile
            </Link>
          </div>
        )}

        {status === "ok" && webinar && (
          <article className="mt-8" data-webinar-id={webinar.id}>
            {/* Hero */}
            <div className="inline-flex items-center gap-2 rounded-full bg-[#F0E7F4] px-3 py-1 text-[11px] font-semibold text-[#965EA7] uppercase tracking-widest">
              <Sparkles size={12} /> {isPast ? "Webinar încheiat" : "Webinar gratuit"}
            </div>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-[#373A45] mt-3 leading-tight">
              {webinar.title}
            </h1>
            {webinar.short_description && (
              <p className="mt-4 text-lg text-[#4b4f5a] leading-relaxed">{webinar.short_description}</p>
            )}

            {/* Details + CTA */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="md:col-span-2 bg-white rounded-3xl border border-[#E5E7EB] p-6 md:p-8">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <MetaRow icon={<CalendarDays size={16} />} label="Data" value={formatWebinarDate(webinar.date)} />
                  <MetaRow
                    icon={<Clock4 size={16} />}
                    label="Ora"
                    value={`${formatTimeRange(webinar.start_time, webinar.duration_minutes)} (${webinar.duration_minutes} min)`}
                  />
                  <MetaRow
                    icon={webinar.location_type === "online" ? <Video size={16} /> : <MapPin size={16} />}
                    label="Format"
                    value={webinar.location_type === "online" ? "Online (link în email)" : "La fața locului"}
                  />
                  <MetaRow
                    icon={<Users size={16} />}
                    label="Locuri disponibile"
                    value={webinar.capacity > 0 ? `${webinar.seats_remaining} / ${webinar.capacity}` : "Limitate"}
                  />
                </dl>
              </div>

              <div className="bg-[#373A45] text-white rounded-3xl p-6 md:p-8 flex flex-col justify-between">
                <div>
                  <div className="text-white/70 text-xs uppercase tracking-widest">Înscriere</div>
                  <div className="mt-2 font-display text-xl font-bold">
                    {isPast ? "Eveniment încheiat" : "Participă gratuit"}
                  </div>
                  <p className="mt-2 text-white/70 text-sm leading-relaxed">
                    {isPast
                      ? "Acest webinar a avut deja loc. Vezi celelalte sesiuni disponibile."
                      : "Linkul de acces îl trimitem pe email cu o zi înainte de eveniment."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={openRegistration}
                  disabled={!canRegister}
                  data-testid="webinar-detail-register"
                  className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold transition-all ${
                    canRegister
                      ? "bg-[#965EA7] hover:bg-[#7A4986] text-white hover:-translate-y-0.5"
                      : "bg-white/20 text-white/50 cursor-not-allowed"
                  }`}
                >
                  {isPast ? "Eveniment încheiat" : isFull ? "Webinar complet" : "Înscrie-te gratuit"}
                </button>
              </div>
            </div>

            {/* Long description */}
            {webinar.full_description && (
              <section className="mt-10" data-testid="webinar-detail-description">
                <h2 className="font-display text-2xl font-bold text-[#373A45]">Despre webinar</h2>
                <div className="mt-4 text-[#4b4f5a] leading-relaxed whitespace-pre-line">
                  {webinar.full_description}
                </div>
              </section>
            )}

            {/* Instructor */}
            {webinar.speaker_name && (
              <section className="mt-10" data-testid="webinar-detail-speaker">
                <h2 className="font-display text-2xl font-bold text-[#373A45]">Despre instructor</h2>
                <div className="mt-4 bg-white rounded-3xl border border-[#E5E7EB] p-6 md:p-8 flex flex-col sm:flex-row gap-5 sm:items-start">
                  <SpeakerAvatar name={webinar.speaker_name} photoUrl={webinar.speaker_photo_url} size={96} />
                  <div>
                    <div className="font-display text-xl font-bold text-[#373A45]">{webinar.speaker_name}</div>
                    {webinar.speaker_title && (
                      <div className="text-sm text-[#965EA7] font-semibold mt-0.5">{webinar.speaker_title}</div>
                    )}
                    {webinar.speaker_bio && (
                      <p className="mt-3 text-[#4b4f5a] leading-relaxed whitespace-pre-line">{webinar.speaker_bio}</p>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* Bottom CTA */}
            {!isPast && (
              <div className="mt-10 bg-[#F0E7F4] rounded-3xl p-7 md:p-9 text-center">
                <CheckCircle2 className="mx-auto text-[#965EA7]" size={28} />
                <h2 className="font-display text-2xl font-bold text-[#373A45] mt-3">
                  Te interesează acest webinar?
                </h2>
                <p className="mt-2 text-[#6B7280] max-w-xl mx-auto">
                  Înscrierea durează 30 de secunde. Primești confirmarea pe email, iar
                  linkul de acces îl trimitem cu o zi înainte de eveniment.
                </p>
                <button
                  type="button"
                  onClick={openRegistration}
                  disabled={!canRegister}
                  data-testid="webinar-detail-register-bottom"
                  className={`mt-5 inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-semibold transition-all ${
                    canRegister
                      ? "bg-[#965EA7] hover:bg-[#7A4986] text-white hover:-translate-y-0.5"
                      : "bg-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed"
                  }`}
                >
                  {isFull ? "Webinar complet" : "Înscrie-te gratuit"}
                </button>
              </div>
            )}
          </article>
        )}
      </main>
      <Footer />

      {registerOpen && webinar && (
        <WebinarRegistrationModal
          webinar={webinar}
          successId={successId}
          onClose={() => { setRegisterOpen(false); setSuccessId(null); }}
          onRegistered={(id) => setSuccessId(id)}
        />
      )}
    </div>
  );
}

function MetaRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <div className="mt-0.5 text-[#965EA7] shrink-0">{icon}</div>
      <div className="text-sm">
        <div className="text-[#6B7280]">{label}</div>
        <div className="font-semibold text-[#373A45] mt-0.5">{value}</div>
      </div>
    </div>
  );
}
